import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CommandMenu.css";
import { useHistory, useLocation } from "react-router-dom";
import { greeting, socialMediaLinks } from "../../portfolio.js";
import { getTheme, setTheme } from "../../utils/theme";

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Quick-jump palette: pages, home sections, actions and external links.
export default function CommandMenu({ open, onClose }) {
  const history = useHistory();
  const { pathname } = useLocation();
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const items = useMemo(() => {
    const email = socialMediaLinks.find((s) => s.name === "Gmail");
    const go = (path, section) => () => {
      const onHome = pathname === "/" || pathname === "/home";
      if (section && onHome && path === "/") return scrollToSection(section);
      history.push(path);
      if (section) setTimeout(() => scrollToSection(section), 450);
      else window.scrollTo({ top: 0 });
    };
    const ext = (href) => () => window.open(href, "_blank", "noopener,noreferrer");
    const list = [
      { group: "Pages", label: "Home", icon: "↗", run: go("/") },
      { group: "Pages", label: "Education", icon: "↗", run: go("/education") },
      { group: "Pages", label: "Experience", icon: "↗", run: go("/experience") },
      { group: "Pages", label: "Projects", icon: "↗", run: go("/projects") },
      { group: "Pages", label: "Contact", icon: "↗", run: go("/contact") },
      { group: "On this site", label: "What I do (skills)", icon: "#", run: go("/", "skills") },
      { group: "On this site", label: "Selected work", icon: "#", run: go("/", "projects") },
      {
        group: "Actions",
        label: getTheme() === "dark" ? "Switch to light mode" : "Switch to dark mode",
        keywords: "theme dark light appearance",
        icon: "◐",
        run: () => setTheme(getTheme() === "dark" ? "light" : "dark"),
      },
      { group: "Actions", label: "Download résumé", icon: "↓", run: ext(greeting.resumeLink) },
    ];
    if (email) list.push({ group: "Actions", label: "Email me", icon: "✉", run: () => (window.location.href = email.link) });
    socialMediaLinks
      .filter((s) => ["Github", "LinkedIn"].includes(s.name))
      .forEach((s) =>
        list.push({ group: "Links", label: s.name === "Github" ? "GitHub" : s.name, icon: "↗", run: ext(s.link) })
      );
    return list;
  }, [history, pathname, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => `${i.label} ${i.keywords || ""} ${i.group}`.toLowerCase().includes(q));
  }, [items, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setIndex(0);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    }
  }, [open]);
  useEffect(() => setIndex(0), [query]);
  useEffect(() => {
    const el = listRef.current && listRef.current.querySelector(".cmd-item.sel");
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [index]);

  if (!open) return null;

  const run = (item) => {
    if (!item) return;
    onClose();
    item.run();
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIndex((i) => (results.length ? (i + 1) % results.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndex((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(results[index]);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  let lastGroup = null;
  return (
    <div className="cmd-overlay" onMouseDown={onClose}>
      <div
        className="cmd"
        role="dialog"
        aria-modal="true"
        aria-label="Quick navigation"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="cmd-input-row">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
          <input
            ref={inputRef}
            autoFocus
            className="cmd-input"
            placeholder="Search pages and actions…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search"
          />
          <kbd>Esc</kbd>
        </div>
        <ul className="cmd-list" ref={listRef} role="listbox">
          {results.length === 0 && <li className="cmd-empty">No results for “{query}”</li>}
          {results.map((item, i) => {
            const head = item.group !== lastGroup;
            lastGroup = item.group;
            return (
              <React.Fragment key={item.group + item.label}>
                {head && <li className="cmd-group">{item.group}</li>}
                <li
                  role="option"
                  aria-selected={i === index}
                  className={`cmd-item${i === index ? " sel" : ""}`}
                  onMouseMove={() => setIndex(i)}
                  onClick={() => run(item)}
                >
                  <span className="cmd-icon">{item.icon}</span>
                  <span className="cmd-label">{item.label}</span>
                  {i === index && <kbd>↵</kbd>}
                </li>
              </React.Fragment>
            );
          })}
        </ul>
        <div className="cmd-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> select</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
